import { fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react'
import { createApi } from '@reduxjs/toolkit/query/react'
import type {
    ApplyV2SettingsSectionName,
    ApplyV2TempProfileCvData,
    GetApplyV2ProfileResponse,
    GetApplyV2SettingsResponse,
} from '@core/app/types/applyV2Type'

export const applyJobV2Api = createApi({
    reducerPath: 'applyJobV2Api',
    baseQuery: fetchBaseQuery({
        baseUrl:
            process.env.ENV === 'local'
                ? `${process.env.API_URL}/api/v2/apply`
                : `${window.location.origin}/api/v2/apply`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            const customCorporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
            if (customCorporation) headers.set('X-CORP-CONTEXT', customCorporation)

            headers.set('Response-V2', '1')
            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['applyJobV2'],
    endpoints: (builder) => ({
        getApplyV2SettingsById: builder.query<GetApplyV2SettingsResponse, { id?: number }>({
            query: ({ id }) => `settings/${id}`,
            providesTags: (result, error, { id }) => (id ? [{ type: 'applyJobV2', id }] : []),
        }),
        getProfileV2Apply: builder.query<GetApplyV2ProfileResponse, { uid?: string }>({
            query: ({ uid }) => ({
                url: 'profile',
                params: { uid },
            }),
            providesTags: (result, error, { uid }) => (uid ? [{ type: 'applyJobV2', id: uid }] : []),
        }),
        postApplyV2SendApplication: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `send?uid=${uid}`,
                method: 'POST',
                // body: {},
            }),
        }),
        postApplyV2ApplicationText: builder.mutation<any, { uid: string; applicationText: string }>({
            query: ({ uid, applicationText }) => ({
                url: `application?uid=${uid}`,
                method: 'POST',
                body: { applicationText },
            }),
        }),
        postApplyV2Attachments: builder.mutation<
            { data: { file: ApplyV2TempProfileCvData }; success: boolean; message: string; statusCode: number },
            { uid: string; file: { name: string; base64: string; ext: string } }
        >({
            query: ({ uid, file }) => ({
                url: `attachment/upload?uid=${uid}`,
                method: 'POST',
                body: { file },
            }),
        }),
        deleteApplyV2Attachments: builder.mutation<any, { uid: string; fileUid: string }>({
            query: ({ uid, fileUid }) => ({
                url: `attachment/delete?uid=${uid}`,
                method: 'DELETE',
                body: { uid: fileUid },
            }),
        }),
        putApplyV2Step: builder.mutation<any, { uid: string; step: ApplyV2SettingsSectionName }>({
            query: ({ uid, step }) => ({
                url: `step?uid=${uid}`,
                method: 'PUT',
                body: { step },
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        initApplyV2: builder.mutation<GetApplyV2ProfileResponse, { id: number | string; body?: any }>({
            query: ({ id, body }) => ({
                url: `init/${id}`,
                method: 'POST',
                body,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        deleteCourseV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `course/delete`,
                method: 'DELETE',
                params: { uid },
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        deleteCourseFileV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `course/file/delete`,
                method: 'DELETE',
                params: { uid },
            }),
        }),
        deleteEducationV2Apply: builder.mutation<any, { uid: string; educationUid: string }>({
            query: ({ uid, educationUid }) => ({
                url: `education/delete`,
                method: 'DELETE',
                params: { uid },
                body: { uid: educationUid },
            }),
        }),
        deleteLanguageV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `language/delete`,
                method: 'DELETE',
                params: { uid },
            }),
        }),
        deleteReferenceV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `reference/delete`,
                method: 'DELETE',
                params: { uid },
            }),
        }),
        deleteSkillV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `skill/delete`,
                method: 'DELETE',
                params: { uid },
            }),
        }),
        deleteWorkExperienceV2Apply: builder.mutation<any, { uid: string; workUid: string }>({
            query: ({ uid, workUid }) => ({
                url: `work-experience/delete`,
                method: 'DELETE',
                params: { uid },
                body: { uid: workUid },
            }),
        }),
        downloadCourseFileV2Apply: builder.mutation<any, { uid: string }>({
            query: ({ uid }) => ({
                url: `course/file/download`,
                method: 'GET',
                params: { uid },
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        getCourseV2Apply: builder.mutation<any, { query: 'Business' }>({
            query: ({ query }) => ({
                url: `course`,
                method: 'GET',
                params: { query },
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        getSkillsV2Apply: builder.query<any, { query: 'Klarna' }>({
            query: ({ query }) => ({
                url: `skills`,
                method: 'GET',
                params: { query },
            }),
            providesTags: (result, error, { query }) => (query ? [{ type: 'applyJobV2', id: query }] : []),
        }),
        postCourseV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    courseName: string
                    courseDescription: string
                    startDate?: string | null
                    endDate?: string | null
                    courseListId?: 0 | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `course/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        postCourseFileV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    file: {
                        name: string
                        ext: string
                        base64: string
                    }
                }
            }
        >({
            query: ({ uid, body }) => ({
                url: `course/file/upload?uid=${uid}`,
                method: 'POST',
                body,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        postCvV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    cv: {
                        name: string
                        ext: string
                        base64: string
                    }
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `cv?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        getApplyV2Socket: builder.query<{ socketUrl: string; [key: string]: any }, { uid: string }>({
            query: ({ uid }) => ({
                url: 'socket',
                method: 'GET',
                params: { uid },
            }),
        }),
        postEducationV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    degree?: string | null
                    toDate: string
                    fromDate: string
                    location?: string
                    schoolName: string
                    description?: string | null
                    educationType?: string | null
                    educationListId?: number | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `education/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        postLanguageV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    worldLanguageId: number
                    languageLevelId: 1 | 2 | 3 | 4 | 5
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `language/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        postLicenseV2Apply: builder.mutation<any, { uid: string; body: { ids: number[] } }>({
            query: ({ body, uid }) => ({
                url: `license?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        postReferenceV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    title: string
                    name: string
                    companyName: string
                    email?: string | null
                    description: string
                    mobilePhone?: {
                        prefix: string
                        number: string
                    } | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `reference/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        postSkillV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    name: string
                    skillTagId: number
                    rating: 0 | 1 | 2 | 3 | 4 | 5
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `skill/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
        }),
        postWorkExperienceV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    title: string
                    companyName: string
                    location?: string | null
                    fromDate: string
                    description?: string
                    titleListId?: 0
                    current: boolean
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `work-experience/create?uid=${uid}`,
                method: 'POST',
                body,
            }),
            // invalidatesTags: (result, error, { id }) => [{ type: 'applyJobV2', id }],
        }),
        putCourseV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    courseName: string
                    courseDescription: string
                    startDate?: string | null
                    endDate?: string | null
                    courseListId?: number | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `course/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putEducationV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    degree?: string | null
                    toDate: string
                    fromDate: string
                    location?: string | null
                    schoolName: string
                    description?: string | null
                    educationType?: string | null
                    educationListId?: number | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `education/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putLanguageV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    languageLevelId: 1 | 2 | 3 | 4 | 5
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `language/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putProfileV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    firstName: string
                    lastName: string
                    email: string
                    title?: string | null
                    gender?: 0 | 1 | 2 | 3 | 4 | null
                    address?: {
                        country: string
                        city: string
                        postalCode: string | number
                        address1: string
                        address2: string
                    } | null
                    mobilePhone?: {
                        prefix: string
                        number: string
                    } | null
                    linkedin?: string | null
                    nationality?: number | null
                    dateOfBirth?: string | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `profile/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putReferenceV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    title: string
                    name: string
                    companyName: string
                    email?: string | null
                    description: string
                    mobilePhone?: {
                        prefix: string
                        number: string
                    } | null
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `reference/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putSkillV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    rating: 0 | 1 | 2 | 3 | 4 | 5
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `skill/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        putWorkExperienceV2Apply: builder.mutation<
            any,
            {
                uid: string
                body: {
                    uid: string
                    title: string
                    companyName: string
                    location?: string | null
                    fromDate: string
                    toDate: string
                    description?: string | null
                    titleListId?: 0
                    current: boolean
                }
            }
        >({
            query: ({ body, uid }) => ({
                url: `work-experience/update?uid=${uid}`,
                method: 'PUT',
                body,
            }),
        }),
        checkEmailDuplicate: builder.mutation<
            {
                statusCode: number
                success: boolean
                message: string
                data: { isUnique: boolean }
            },
            { email: string }
        >({
            query: ({ email }) => ({
                url: `duplicate/email`,
                method: 'POST',
                body: { email },
            }),
        }),

        checkPhoneDuplicate: builder.mutation<
            {
                statusCode: number
                success: boolean
                message: string
                data: { isUnique: boolean }
            },
            { mobilePhone: { prefix: string; number: string } }
        >({
            query: ({ mobilePhone }) => ({
                url: `duplicate/phone`,
                method: 'POST',
                body: { mobilePhone },
            }),
        }),
    }),
})

export const {
    useGetApplyV2SettingsByIdQuery,
    useGetProfileV2ApplyQuery,
    useInitApplyV2Mutation,
    useDeleteCourseV2ApplyMutation,
    useDeleteCourseFileV2ApplyMutation,
    useDeleteEducationV2ApplyMutation,
    useDeleteLanguageV2ApplyMutation,
    useDeleteReferenceV2ApplyMutation,
    useDeleteSkillV2ApplyMutation,
    useDeleteWorkExperienceV2ApplyMutation,
    useDownloadCourseFileV2ApplyMutation,
    useGetCourseV2ApplyMutation,
    useGetSkillsV2ApplyQuery,
    usePostCourseV2ApplyMutation,
    usePostCourseFileV2ApplyMutation,
    usePostCvV2ApplyMutation,
    useGetApplyV2SocketQuery,
    useLazyGetApplyV2SocketQuery,
    usePostEducationV2ApplyMutation,
    usePostLanguageV2ApplyMutation,
    usePostLicenseV2ApplyMutation,
    usePostReferenceV2ApplyMutation,
    usePostSkillV2ApplyMutation,
    usePostWorkExperienceV2ApplyMutation,
    usePutCourseV2ApplyMutation,
    usePutEducationV2ApplyMutation,
    usePutLanguageV2ApplyMutation,
    usePutProfileV2ApplyMutation,
    usePutReferenceV2ApplyMutation,
    usePutSkillV2ApplyMutation,
    usePutWorkExperienceV2ApplyMutation,
    useCheckEmailDuplicateMutation,
    useCheckPhoneDuplicateMutation,
    usePostApplyV2ApplicationTextMutation,
    usePostApplyV2AttachmentsMutation,
    useDeleteApplyV2AttachmentsMutation,
    usePutApplyV2StepMutation,
    usePostApplyV2SendApplicationMutation,
} = applyJobV2Api
